import { HttpAttachment } from "tekkr-common/dist/model/attachments";
import { useRemoveProjectAttachmentMutation } from "../../../data/mutations/remove-project-attachment";
import React, { useEffect } from "react";
import { useAttachmentDisplay } from "../../../lib/attachments/attachments";
import { OptionalLink } from "../../../components/shared/optional-link";
import { cn } from "../../../lib/utils";
import { UserAvatarAndName } from "../../../components/shared/org/avatar-for-user";
import { DateTime } from "luxon";
import ConfirmationDialog from "../../../components/modals/confirmation-dialog";
import { DialogTrigger } from "@radix-ui/react-dialog";
import { Button } from "../../../components/ui/button";
import { XIcon } from "lucide-react";
import { AttachmentReferenceType } from "tekkr-common/dist/enums";
import { useGetFileContentsQuery } from "../../../data/queries/file-contents";
import { Dialog, DialogContent } from "../../../components/ui/dialog";
import Spinner from "../../../components/ui/spinner";

export function AttachmentList(props: { attachments: HttpAttachment[]; className?: string }) {
    return (
        <div className={cn("flex flex-col gap-3 overflow-y-scroll", props.className)}>
            {props.attachments.map((a) => {
                return <AttachmentItem attachment={a} key={a.id} />;
            })}
        </div>
    );
}

function AttachmentItem(props: { attachment: HttpAttachment }) {
    const removeAttachmentMutation = useRemoveProjectAttachmentMutation();
    const removeAttachment = () =>
        removeAttachmentMutation.mutateAsync({
            attachmentId: props.attachment.id,
        });
    const isFile = isFileAttachment(props.attachment);
    const [showFilePreview, setShowFilePreview] = React.useState(false);

    const { icon: Icon, url, type } = useAttachmentDisplay(props.attachment);

    return (
        <div className={"flex flex-row items-center gap-4 rounded-md bg-accent px-4 py-2"}>
            {showFilePreview && isFileAttachment(props.attachment) && (
                <FilePreview attachment={props.attachment} onClose={() => setShowFilePreview(false)} />
            )}
            <OptionalLink to={url} target={"_blank"} rel={"noreferrer"} className={"grow"}>
                <div
                    onClick={isFile ? () => setShowFilePreview(true) : undefined}
                    className={cn("flex grow flex-row items-center gap-3", isFile && "cursor-pointer")}
                >
                    <Icon className={"h-5 w-5 shrink-0"} />
                    <div className={"flex grow flex-col items-start gap-0.5"}>
                        <div className={"line-clamp-2 flex flex-row items-center gap-2 text-sm font-semibold"}>
                            {props.attachment.name}
                        </div>
                        <div
                            className={
                                "flex flex-row flex-wrap items-center gap-1 text-xs font-medium text-muted-foreground"
                            }
                        >
                            {type} added by
                            <UserAvatarAndName userId={props.attachment.createdBy} avatarClassName={"w-4 h-4"} />
                            <div>
                                on {DateTime.fromISO(props.attachment.createdAt).toLocaleString(DateTime.DATE_MED)}
                            </div>
                        </div>
                    </div>
                </div>
            </OptionalLink>

            <ConfirmationDialog
                title={"Remove Attachment"}
                body={
                    <div className={"flex flex-col gap-2 pb-3"}>
                        Are you sure you want to remove this attachment?
                        <div className={"text-sm text-muted-foreground"}>
                            This will remove the attachment everywhere but not delete the source file. (e.g. your Google
                            Doc)
                        </div>
                    </div>
                }
                confirm={removeAttachment}
            >
                <DialogTrigger>
                    <Button variant={"ghost-destructive"} size={"sm"}>
                        <XIcon className={"h-4 w-4"} />
                    </Button>
                </DialogTrigger>
            </ConfirmationDialog>
        </div>
    );
}

export type FileAttachment = HttpAttachment & {
    reference: Extract<HttpAttachment["reference"], { type: AttachmentReferenceType.File }>;
};
export function isFileAttachment(attachment: HttpAttachment): attachment is FileAttachment {
    return attachment.reference.type === AttachmentReferenceType.File;
}

function toDataSrc(file: { mimeType: string; base64Data: string }) {
    return `data:${file.mimeType};base64,${file.base64Data}`;
}

export function FilePreview({ attachment, onClose }: { attachment: FileAttachment; onClose?: () => void }) {
    const content = useGetFileContentsQuery(attachment.reference.fileId);

    const isImage = content.data?.mimeType.startsWith("image/");
    const isPdf = content.data?.mimeType.startsWith("application/pdf");
    const isOther = !isImage && !isPdf;
    useEffect(() => {
        if (isOther && content.data) {
            const link = document.createElement("a");
            link.href = toDataSrc(content.data);
            link.download = attachment.name;
            link.click();
            onClose?.();
        }
    });

    if (content.isPending) {
        return (
            <Dialog open={true}>
                <DialogContent disableClose={true}>
                    <div className={"flex flex-row items-center justify-center gap-4 p-4 font-medium"}>
                        <Spinner /> Getting file data...
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    if (!content.data) {
        return null;
    }

    if (isImage) {
        return (
            <Dialog open={true} onOpenChange={onClose}>
                <DialogContent className={"max-h-[95svh] overflow-y-scroll"}>
                    <div className={"flex h-full w-full flex-col gap-3"}>
                        <div className={"-mt-3 text-sm font-medium"}>{attachment.name}</div>
                        <img alt={attachment.name} src={toDataSrc(content.data)} />
                    </div>
                </DialogContent>
            </Dialog>
        );
    } else if (isPdf) {
        return (
            <Dialog open={true} onOpenChange={onClose}>
                <DialogContent className={"h-full max-h-[95svh] w-full max-w-[95svw]"}>
                    <div className={"flex h-full w-full flex-col gap-3"}>
                        <div className={"-mt-3 text-sm font-medium"}>{attachment.name}</div>
                        <embed className={"grow"} type="application/pdf" src={toDataSrc(content.data)} />
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    return <></>;
}
